import { makeAutoObservable } from 'mobx';
import retryDelay from '../../../../utils/retryDelay';
import {
  MY_NOTIFICATIONS_STREAM_API_PATH,
  MY_NOTIFICATIONS_UNREAD_COUNT_API_PATH,
} from '../apiPaths';

const REFRESH_EVENTS = new Set(['notifications.sync', 'notifications.changed']);

export default class NotificationInboxStore {
  unreadCount = 0;

  refreshRetryAttempt = 0;

  mutationToken = 0;

  notificationReads = new Map();

  markAllReadPending = false;

  notificationReadBaseCount = 0;

  nextNotificationReadToken = 1;

  constructor() {
    makeAutoObservable(this, {
      abortController: false,
      notificationReads: false,
      provider: false,
      refreshPromise: false,
      refreshers: false,
      retryTimer: false,
    });
    this.abortController = null;
    this.provider = null;
    this.refreshPromise = null;
    this.refreshers = new Set();
    this.retryTimer = null;
    this.session = 0;
    this.refreshPending = false;
  }

  connect(provider, onError) {
    if (!provider || this.provider === provider) {
      return;
    }

    this.disconnect();
    this.provider = provider;
    this.session += 1;
    const { session } = this;
    this.abortController = new AbortController();
    const { abortController } = this;
    provider
      .subscribe(MY_NOTIFICATIONS_STREAM_API_PATH, {
        signal: abortController.signal,
        onError: error => {
          if (session === this.session && !abortController.signal.aborted) {
            onError?.(error);
          }
        },
        onMessage: message => {
          if (session === this.session && REFRESH_EVENTS.has(message?.event)) {
            this.requestRefresh();
          }
        },
      })
      .catch(() => {
        if (session === this.session && !this.abortController?.signal.aborted) {
          this.scheduleRefreshRetry();
        }
      });
    this.requestRefresh();
  }

  disconnect() {
    this.abortController?.abort();
    this.abortController = null;
    this.provider = null;
    this.refreshPending = false;
    this.refreshPromise = null;
    this.refreshRetryAttempt = 0;
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  registerRefresh(refresh) {
    this.refreshers.add(refresh);
    return () => this.refreshers.delete(refresh);
  }

  requestRefresh() {
    if (!this.provider) {
      return Promise.resolve(false);
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.refreshPending = true;
    if (!this.refreshPromise) {
      this.refreshPromise = this.runRefreshes();
    }
    return this.refreshPromise;
  }

  async runRefreshes() {
    const { session } = this;
    let allSucceeded = true;

    while (this.refreshPending && session === this.session) {
      this.refreshPending = false;
      if (this.notificationReads.size > 0 || this.markAllReadPending) {
        this.refreshPending = true;
        this.scheduleRefreshRetry();
        // Let requestRefresh store this promise before the cleanup clears it.
        // eslint-disable-next-line no-await-in-loop
        await Promise.resolve();
        break;
      }
      const issuedToken = this.currentToken();
      const { provider } = this;
      const tasks = [
        provider.get(MY_NOTIFICATIONS_UNREAD_COUNT_API_PATH, null, {
          suppressGlobalErrorRedirect: true,
          allowTokenRenewal: true,
          signal: this.abortController?.signal,
        }),
        ...[...this.refreshers].map(refresh =>
          refresh(this.abortController?.signal),
        ),
      ];
      // eslint-disable-next-line no-await-in-loop
      const results = await Promise.allSettled(tasks);
      const [countResult] = results;
      const countResponse =
        countResult.status === 'fulfilled' ? countResult.value : null;
      const succeeded =
        countResponse?.ok &&
        results
          .slice(1)
          .every(
            result => result.status === 'fulfilled' && result.value !== false,
          );

      if (session !== this.session) {
        return false;
      }
      if (!succeeded) {
        allSucceeded = false;
        this.refreshPending = true;
        this.scheduleRefreshRetry();
        break;
      }

      this.refreshRetryAttempt = 0;
      const count =
        countResponse?.data?.unreadCount ??
        countResponse?.data?.data?.unreadCount;
      this.setUnreadCount(count, issuedToken);
    }

    if (session === this.session) {
      this.refreshPromise = null;
      if (!this.refreshPending && this.retryTimer) {
        clearTimeout(this.retryTimer);
        this.retryTimer = null;
      }
    }
    return allSucceeded;
  }

  scheduleRefreshRetry() {
    if (this.retryTimer || !this.provider) {
      return;
    }
    const delay = retryDelay(this.refreshRetryAttempt);
    this.refreshRetryAttempt += 1;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.requestRefresh();
    }, delay);
  }

  currentToken() {
    return this.mutationToken;
  }

  invalidatePendingReads() {
    this.mutationToken += 1;
  }

  beginNotificationRead(notificationId, optimisticUnreadCount) {
    if (this.notificationReads.has(notificationId)) {
      return null;
    }
    if (this.notificationReads.size === 0) {
      this.notificationReadBaseCount = this.unreadCount;
    }
    const operationToken = this.nextNotificationReadToken;
    this.nextNotificationReadToken += 1;
    this.notificationReads.set(notificationId, {
      operationToken,
      unreadCount: optimisticUnreadCount,
    });
    this.invalidatePendingReads();
    this.reconcileNotificationReads();
    return operationToken;
  }

  completeNotificationRead(notificationId, operationToken, markedReadCount) {
    const operation = this.notificationReads.get(notificationId);
    if (!operation || operation.operationToken !== operationToken) {
      return false;
    }
    const effectiveCount =
      typeof markedReadCount === 'number'
        ? markedReadCount
        : operation.unreadCount;
    this.notificationReadBaseCount = Math.max(
      0,
      this.notificationReadBaseCount - effectiveCount,
    );
    this.notificationReads.delete(notificationId);
    this.invalidatePendingReads();
    this.reconcileNotificationReads();
    return true;
  }

  failNotificationRead(notificationId, operationToken) {
    const operation = this.notificationReads.get(notificationId);
    if (!operation || operation.operationToken !== operationToken) {
      return false;
    }
    this.notificationReads.delete(notificationId);
    this.invalidatePendingReads();
    this.reconcileNotificationReads();
    return true;
  }

  reconcileNotificationReads() {
    const pendingUnreadCount = [...this.notificationReads.values()].reduce(
      (sum, operation) => sum + operation.unreadCount,
      0,
    );
    this.unreadCount = Math.max(
      0,
      this.notificationReadBaseCount - pendingUnreadCount,
    );
  }

  setUnreadCount(count, issuedToken) {
    if (
      this.notificationReads.size > 0 ||
      (issuedToken !== undefined && issuedToken !== this.mutationToken)
    ) {
      return;
    }
    this.unreadCount = Math.max(0, count ?? 0);
  }

  clearUnreadCount() {
    this.invalidatePendingReads();
    this.notificationReads.clear();
    this.notificationReadBaseCount = 0;
    this.unreadCount = 0;
  }

  beginMarkAllRead() {
    if (this.markAllReadPending) {
      return false;
    }
    this.markAllReadPending = true;
    this.invalidatePendingReads();
    return true;
  }

  completeMarkAllRead() {
    if (!this.markAllReadPending) {
      return false;
    }
    this.markAllReadPending = false;
    this.clearUnreadCount();
    return true;
  }

  failMarkAllRead() {
    if (!this.markAllReadPending) {
      return false;
    }
    this.markAllReadPending = false;
    this.invalidatePendingReads();
    return true;
  }

  clearStore() {
    this.session += 1;
    this.disconnect();
    this.markAllReadPending = false;
    this.clearUnreadCount();
  }
}
